/* ==========================================================================
   Eddie's Plumbing & Heating - motion controller.
   Vanilla, no dependencies, ~9KB unminified. Drives the primitives declared
   in animations.css.

   Contract:
     - IntersectionObserver for everything scroll-triggered. The single raw
       scroll listener is rAF-throttled and passive, and only writes CSS
       custom properties (progress bar, pipe temperature, flow speed).
     - prefers-reduced-motion short-circuits every optional behaviour.
     - Off-screen sections get data-paused="true" so nothing animates unseen.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var still = reduce.matches;
  reduce.addEventListener('change', function (e) {
    still = e.matches;
  });

  var $ = function (sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  };
  var raf = window.requestAnimationFrame.bind(window);

  /* ----------------------------------------------------------------------
     1 + 2. Scroll reveal with automatic sibling stagger.
     ---------------------------------------------------------------------- */
  function initReveals() {
    var items = $('.reveal');
    if (!items.length) return;

    // Auto-stagger: siblings sharing a parent step 90ms apart, capped at 540ms.
    var seen = new Map();
    items.forEach(function (el) {
      if (el.style.getPropertyValue('--reveal-delay')) return;
      var p = el.parentElement;
      var i = seen.get(p) || 0;
      seen.set(p, i + 1);
      el.style.setProperty('--reveal-delay', Math.min(i * 90, 540) + 'ms');
    });

    if (still) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach(function (el) {
      io.observe(el);
    });

    // Safety net. Reveal-on-scroll must never be the reason a homeowner
    // cannot read the page: if the observer has not fired for something
    // after 3s (embedded in a frame, restored from bfcache, an engine quirk),
    // show it anyway.
    setTimeout(function () {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
    }, 3000);
  }

  /* ----------------------------------------------------------------------
     3. Word-by-word heading reveal. Only splits headings with plain text.
     ---------------------------------------------------------------------- */
  function initSplit() {
    if (still) return;
    var split = [];

    $('[data-split]').forEach(function (el) {
      if (el.children.length) return;
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      words.forEach(function (w, i) {
        var span = document.createElement('span');
        span.className = 'split-word';
        span.style.setProperty('--word-i', i);
        span.textContent = w;
        el.appendChild(span);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
      split.push(el);
    });
    if (!split.length) return;

    // A split heading reveals itself. It used to rely on sitting inside a
    // .reveal, which meant any heading outside one (the page headers) stayed
    // at opacity 0 forever.
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        });
      },
      { threshold: 0.15 }
    );
    split.forEach(function (el) {
      io.observe(el);
    });

    setTimeout(function () {
      split.forEach(function (el) {
        el.classList.add('is-visible');
      });
    }, 3000);
  }

  /* ----------------------------------------------------------------------
     Hero load orchestration. Line spans are already in the markup; this
     just releases them once fonts are settled so nothing reflows mid-lift.
     ---------------------------------------------------------------------- */
  function initHero() {
    var hero = document.querySelector('.hero');
    if (!hero) return;
    var go = function () {
      hero.classList.add('is-loaded');
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(go);
      setTimeout(go, 900); // never let a font stall the headline
    } else {
      go();
    }
  }

  /* ----------------------------------------------------------------------
     Pointer parallax. The hero layers lean toward the cursor at rates set
     by their data-depth. The value is LERPed rather than snapped: the lag
     is what makes it feel like weight rather than a mouse-follower.

     Writes two custom properties and nothing else. No state, no re-layout,
     one rAF loop that idles when the pointer has settled.
     ---------------------------------------------------------------------- */
  function initParallax() {
    var host = document.querySelector('[data-parallax]');
    if (!host || still) return;
    if (!window.matchMedia('(hover: hover) and (min-width: 900px)').matches) return;

    var tx = 0, ty = 0, cx = 0, cy = 0, running = false;

    function frame() {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      host.style.setProperty('--px', cx.toFixed(4));
      host.style.setProperty('--py', cy.toFixed(4));
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) {
        raf(frame);
      } else {
        running = false;
      }
    }

    window.addEventListener(
      'pointermove',
      function (e) {
        var r = host.getBoundingClientRect();
        if (e.clientY < r.top || e.clientY > r.bottom) return;
        tx = (e.clientX / window.innerWidth) * 2 - 1;
        ty = (e.clientY - r.top) / r.height * 2 - 1;
        if (!running) {
          running = true;
          raf(frame);
        }
      },
      { passive: true }
    );

    // Settle back to centre when the pointer leaves the window.
    document.addEventListener('pointerleave', function () {
      tx = 0;
      ty = 0;
      if (!running) {
        running = true;
        raf(frame);
      }
    });
  }

  /* ----------------------------------------------------------------------
     Magnetic buttons. Same technique, per element, capped so the label
     never drifts far enough to be hard to hit.
     ---------------------------------------------------------------------- */
  function initMagnetic() {
    if (still) return;
    if (!window.matchMedia('(hover: hover) and (min-width: 900px)').matches) return;

    $('[data-magnetic]').forEach(function (el) {
      el.addEventListener(
        'pointermove',
        function (e) {
          var r = el.getBoundingClientRect();
          var dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
          var dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
          el.style.setProperty('--mx', (dx * 7).toFixed(2) + 'px');
          el.style.setProperty('--my', (dy * 5).toFixed(2) + 'px');
        },
        { passive: true }
      );
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      });
    });
  }

  /* ----------------------------------------------------------------------
     Animated counters. Fires once, eases out over 1400ms.
     ---------------------------------------------------------------------- */
  function initCounters() {
    var nodes = $('[data-count]');
    if (!nodes.length) return;
    var run = function (el) {
      var target = parseFloat(el.dataset.count) || 0;
      var dec = (el.dataset.count.split('.')[1] || '').length;
      if (still) {
        el.textContent = target.toFixed(dec);
        return;
      }
      var t0 = performance.now();
      var tick = function (now) {
        var p = Math.min((now - t0) / 1400, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = (target * eased).toFixed(dec);
        if (p < 1) raf(tick);
      };
      raf(tick);
    };
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          run(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.5 }
    );
    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Marquees: duplicate the track content once so the loop is seamless.
     ---------------------------------------------------------------------- */
  function initMarquees() {
    $('.marquee__track, .emergency__track').forEach(function (track) {
      if (track.dataset.doubled) return;
      track.dataset.doubled = '1';
      var clone = track.cloneNode(true);
      clone.querySelectorAll('*').forEach(function (n) {
        n.setAttribute('aria-hidden', 'true');
        if (n.tagName === 'A' || n.tagName === 'BUTTON') n.tabIndex = -1;
      });
      while (clone.firstChild) track.appendChild(clone.firstChild);
    });
  }

  /* ----------------------------------------------------------------------
     Pause anything off-screen. Nothing animates in the background.
     ---------------------------------------------------------------------- */
  function initPause() {
    var targets = $('.section, .hero, .emergency, .footer, .service-hero');
    if (!targets.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          e.target.dataset.paused = e.isIntersecting ? 'false' : 'true';
        });
      },
      { rootMargin: '120px 0px' }
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ======================================================================
     THE PRESSURE LINE
     One continuous pipe, hero to footer. Routes left/right around the
     sections, carries fluid that runs cold at the top and hot at the
     bottom, and speeds up when the visitor scrolls fast.
     ====================================================================== */
  var Pipe = {
    host: null,
    svg: null,
    paths: [],
    gauges: [],
    height: 0,

    init: function () {
      this.host = document.querySelector('.pressure-line');
      if (!this.host) return;
      this.svg = this.host.querySelector('svg');
      this.build();
      var ro = new ResizeObserver(this.build.bind(this));
      ro.observe(document.body);
    },

    // Anchor points come from real section boundaries so the pipe threads
    // between content rather than cutting through it.
    build: function () {
      var h = document.body.scrollHeight;
      if (Math.abs(h - this.height) < 24 && this.svg.dataset.built) return;
      this.height = h;
      var w = window.innerWidth;
      var narrow = w < 768;
      var inset = narrow ? [5, 11] : [11, 89];

      this.host.style.height = h + 'px';
      this.svg.setAttribute('viewBox', '0 0 100 ' + h);
      this.svg.setAttribute('preserveAspectRatio', 'none');

      var stops = $('.section, .hero, .emergency, .footer, .service-hero')
        .map(function (el) {
          var r = el.getBoundingClientRect();
          return r.top + window.scrollY + r.height / 2;
        })
        .filter(function (y) {
          return y > 0;
        })
        .sort(function (a, b) {
          return a - b;
        });
      if (stops.length < 2) stops = [0, h * 0.5, h];

      var d = 'M ' + inset[0] + ' 0';
      var prevX = inset[0];
      var prevY = 0;
      var self = this;
      var gaugeAt = [];
      stops.forEach(function (y, i) {
        var x = inset[i % 2];
        var midY = (prevY + y) / 2;
        d += ' C ' + prevX + ' ' + midY + ', ' + x + ' ' + midY + ', ' + x + ' ' + y;
        if (i % 2 === 1 && !narrow) gaugeAt.push({ x: x, y: y });
        prevX = x;
        prevY = y;
      });
      d += ' L ' + prevX + ' ' + h;

      this.svg.innerHTML = '';
      ['casing', 'bore', 'fluid'].forEach(function (kind) {
        var p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        p.setAttribute('d', d);
        p.setAttribute('vector-effect', 'non-scaling-stroke');
        p.setAttribute('class', 'pressure-line__' + kind);
        self.svg.appendChild(p);
      });
      this.svg.dataset.built = '1';
      this.buildGauges(gaugeAt, narrow);
    },

    // A gauge joint at every other section boundary. Needle sweeps and the
    // PSI readout counts up as that joint enters the viewport.
    buildGauges: function (spots, narrow) {
      var self = this;
      this.gauges = [];
      if (narrow) return;
      spots.forEach(function (s, i) {
        var psi = 40 + ((i * 17) % 55);
        var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        g.setAttribute('class', 'gauge');
        g.setAttribute(
          'transform',
          'translate(' + s.x + ' ' + s.y + ') scale(0.34 1)'
        );
        g.innerHTML =
          '<circle class="gauge__face" r="26"/>' +
          '<circle class="gauge__ring" r="20"/>' +
          '<line class="gauge__needle" x1="0" y1="4" x2="0" y2="-15"/>' +
          '<text class="gauge__psi" x="0" y="46" text-anchor="middle">0 PSI</text>';
        self.svg.appendChild(g);
        self.gauges.push({ el: g, psi: psi, done: false });
      });

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            var rec = self.gauges.filter(function (r) {
              return r.el === e.target;
            })[0];
            if (!e.isIntersecting || !rec || rec.done) return;
            rec.done = true;
            var needle = rec.el.querySelector('.gauge__needle');
            var label = rec.el.querySelector('.gauge__psi');
            var deg = -52 + (rec.psi / 100) * 104;
            if (still) {
              needle.style.rotate = deg + 'deg';
              label.textContent = rec.psi + ' PSI';
              return;
            }
            needle.style.rotate = deg + 'deg';
            var t0 = performance.now();
            var tick = function (now) {
              var p = Math.min((now - t0) / 1100, 1);
              label.textContent = Math.round(rec.psi * (1 - Math.pow(1 - p, 3))) + ' PSI';
              if (p < 1) raf(tick);
            };
            raf(tick);
          });
        },
        { threshold: 0.4 }
      );
      this.gauges.forEach(function (r) {
        io.observe(r.el);
      });
    }
  };

  /* ----------------------------------------------------------------------
     The one raw scroll listener: rAF-throttled, passive, writes only custom
     properties. Drives progress bar, pipe temperature, and flow speed.
     ---------------------------------------------------------------------- */
  function initScrollDriver() {
    var bar = document.querySelector('.progress');
    var header = document.querySelector('.header');
    var spines = $('[data-spine]');
    var lastY = window.scrollY;
    var lastT = performance.now();
    var queued = false;

    var mix = function (a, b, t) {
      return a.map(function (v, i) {
        return Math.round(v + (b[i] - v) * t);
      });
    };
    var COLD = [56, 189, 248];
    var WARM = [254, 124, 0];
    var HOT = [255, 77, 31];

    function update() {
      queued = false;
      var y = window.scrollY;
      var max = document.body.scrollHeight - window.innerHeight;
      var p = max > 0 ? Math.min(Math.max(y / max, 0), 1) : 0;

      if (bar) bar.style.transform = 'scaleX(' + p + ')';
      if (header) header.classList.toggle('is-condensed', y > 40);

      // Cold at the top (plumbing), hot at the bottom (heating).
      var rgb = p < 0.5 ? mix(COLD, WARM, p / 0.5) : mix(WARM, HOT, (p - 0.5) / 0.5);
      root.style.setProperty('--line-temp', 'rgb(' + rgb.join(' ') + ')');

      if (!still) {
        // Scroll velocity feeds flow speed. Fast scroll, fast fluid.
        var now = performance.now();
        var dt = Math.max(now - lastT, 16);
        var v = Math.abs(y - lastY) / dt; // px per ms
        lastY = y;
        lastT = now;
        var speed = Math.min(1 + v * 0.45, 3.2);
        root.style.setProperty('--flow-speed', speed.toFixed(2));
      }

      // Timeline spine draws against its own progress through the viewport.
      spines.forEach(function (el) {
        var r = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var prog = 1 - (r.top - vh * 0.25) / (vh * 0.75);
        el.style.setProperty('--progress', Math.min(Math.max(prog, 0), 1).toFixed(3));
      });
    }

    var onScroll = function () {
      if (queued) return;
      queued = true;
      raf(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ----------------------------------------------------------------------
     Mobile menu. Focus trap + Esc.
     ---------------------------------------------------------------------- */
  function initMenu() {
    var menu = document.querySelector('.menu');
    var burger = document.querySelector('.header__burger');
    if (!menu || !burger) return;
    var lastFocus = null;

    function setOpen(open) {
      menu.dataset.open = open ? 'true' : 'false';
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        lastFocus = document.activeElement;
        var first = menu.querySelector('a, button');
        if (first) first.focus();
      } else if (lastFocus) {
        lastFocus.focus();
      }
    }

    burger.addEventListener('click', function () {
      setOpen(menu.dataset.open !== 'true');
    });
    $('.menu__close, .menu a', menu).forEach(function (el) {
      el.addEventListener('click', function () {
        setOpen(false);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (menu.dataset.open !== 'true') return;
      if (e.key === 'Escape') return setOpen(false);
      if (e.key !== 'Tab') return;
      var f = $('a, button', menu).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  /* ----------------------------------------------------------------------
     Testimonial carousel.
     ---------------------------------------------------------------------- */
  function initQuotes() {
    var wrap = document.querySelector('.quotes');
    if (!wrap) return;
    var slides = $('.quote', wrap);
    var dots = $('.quotes__dot', wrap);
    if (slides.length < 2) return;
    var i = 0;
    var timer = null;

    function show(n) {
      i = (n + slides.length) % slides.length;
      slides.forEach(function (s, k) {
        s.dataset.active = k === i ? 'true' : 'false';
      });
      dots.forEach(function (d, k) {
        d.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
    }
    dots.forEach(function (d, k) {
      d.addEventListener('click', function () {
        show(k);
        restart();
      });
    });
    function restart() {
      clearInterval(timer);
      if (!still) timer = setInterval(function () {
        show(i + 1);
      }, 7000);
    }
    wrap.addEventListener('mouseenter', function () {
      clearInterval(timer);
    });
    wrap.addEventListener('mouseleave', restart);
    show(0);
    restart();
  }

  /* ----------------------------------------------------------------------
     Gallery filter + lightbox (focus trapped, Esc + arrow keys).
     ---------------------------------------------------------------------- */
  function initGallery() {
    var grid = document.querySelector('.gallery');
    if (!grid) return;
    var items = $('.gallery__item', grid);

    $('.filters button').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var f = btn.dataset.filter;
        $('.filters button').forEach(function (b) {
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
        items.forEach(function (it) {
          it.hidden = !(f === 'all' || it.dataset.tags.indexOf(f) > -1);
        });
      });
    });

    var box = document.querySelector('.lightbox');
    if (!box) return;
    var img = box.querySelector('img');
    var cur = 0;
    var lastFocus = null;

    function open(n) {
      var visible = items.filter(function (it) {
        return !it.hidden;
      });
      cur = (n + visible.length) % visible.length;
      var src = visible[cur].querySelector('img');
      img.src = src.dataset.full || src.src;
      img.alt = src.alt;
      lastFocus = document.activeElement;
      box.setAttribute('open', '');
      box.querySelector('.lightbox__close').focus();
    }
    function close() {
      box.removeAttribute('open');
      if (lastFocus) lastFocus.focus();
    }
    items.forEach(function (it, n) {
      it.addEventListener('click', function () {
        open(
          items.filter(function (x) {
            return !x.hidden;
          }).indexOf(it)
        );
      });
    });
    box.querySelector('.lightbox__close').addEventListener('click', close);
    box.querySelector('.lightbox__prev').addEventListener('click', function () {
      open(cur - 1);
    });
    box.querySelector('.lightbox__next').addEventListener('click', function () {
      open(cur + 1);
    });
    document.addEventListener('keydown', function (e) {
      if (!box.hasAttribute('open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') open(cur - 1);
      if (e.key === 'ArrowRight') open(cur + 1);
      if (e.key === 'Tab') {
        var f = $('button', box);
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* ----------------------------------------------------------------------
     Contact form: inline validation, emergency redirect to the phone,
     loading state on submit.
     ---------------------------------------------------------------------- */
  function initForm() {
    $('form.eddie-form').forEach(function (form) {
      // `required` stays in the markup for assistive tech, but the browser's
      // own validation bubble is suppressed so every error is reported the
      // same way: inline, under the field, in plain language.
      form.setAttribute('novalidate', '');

      var panic = form.querySelector('.form__emergency');
      var toggle = form.querySelector('[data-emergency-toggle]');
      if (toggle && panic) {
        toggle.addEventListener('change', function () {
          panic.dataset.show = toggle.checked ? 'true' : 'false';
        });
      }

      function validate(field) {
        var wrap = field.closest('.field');
        if (!wrap) return true;
        var err = wrap.querySelector('.field__error');
        var msg = '';
        if (field.required && !field.value.trim()) {
          msg = 'This field is required.';
        } else if (field.type === 'email' && field.value && !/^\S+@\S+\.\S+$/.test(field.value)) {
          msg = 'Enter an email address like name@example.com.';
        } else if (field.type === 'tel' && field.value && field.value.replace(/\D/g, '').length < 10) {
          msg = 'Enter a 10-digit phone number so we can call you back.';
        }
        field.setAttribute('aria-invalid', msg ? 'true' : 'false');
        if (err) err.textContent = msg;
        return !msg;
      }

      $('input, select, textarea', form).forEach(function (field) {
        field.addEventListener('blur', function () {
          validate(field);
        });
        field.addEventListener('input', function () {
          if (field.getAttribute('aria-invalid') === 'true') validate(field);
        });
      });

      form.addEventListener('submit', function (e) {
        // map, not every: `every` short-circuits on the first failure, which
        // would flag one field and leave the rest silently broken.
        var results = $('input, select, textarea', form).map(validate);
        var ok = results.indexOf(false) === -1;
        if (!ok) {
          e.preventDefault();
          var bad = form.querySelector('[aria-invalid="true"]');
          if (bad) bad.focus();
          return;
        }
        var btn = form.querySelector('[type="submit"]');
        if (btn) {
          btn.setAttribute('aria-busy', 'true');
          btn.textContent = 'Sending...';
        }
      });
    });
  }

  /* ---------------------------------------------------------------------- */
  function boot() {
    initReveals();
    initSplit();
    initHero();
    initCounters();
    initParallax();
    initMagnetic();
    initMarquees();
    initPause();
    initMenu();
    initQuotes();
    initGallery();
    initForm();
    initScrollDriver();
    Pipe.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
